import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { daysToRelease } from '../studioRules';

/** The moment of a song that sets its "Next up" chips (design t1). */
export type NextUpMoment = 'noMaster' | 'beforeRelease' | 'outNow' | 'catalog';

export interface NextUp {
  moment: NextUpMoment;
  /** Two asset types, in the order the card shows them. */
  assetTypeIds: readonly [StudioThemeId, StudioThemeId];
}

/** A song that came out in the last 60 days is "out now"; an older song is catalog. */
export const NEXT_UP_OUT_NOW_DAYS = 60;

/**
 * The two asset types that fit the moment of a song (design t1, "Next up"). Pure.
 *
 * - No usable master (none, or it failed): two images that need no master.
 * - Before the release: the countdown and the pre-save story. An unreleased song with no date
 *   gets the pre-save story and the announce post, because a countdown needs a date.
 * - Out for 60 days or less: the out-now story and the visualizer.
 * - Older, or a released song with no date: the Canvas loop and the visualizer.
 */
export function nextUpAssetTypes(item: CatalogItem, now: Date): NextUp {
  const { hasMaster, masterStatus } = item.song;
  if (!hasMaster || masterStatus === null || masterStatus === 'failed') {
    return { moment: 'noMaster', assetTypeIds: ['feedPost', 'linkPreview'] };
  }
  const days = daysToRelease(item.releaseDate, now);
  if (days === null) {
    return item.kind === CatalogItemKind.unreleased
      ? { moment: 'beforeRelease', assetTypeIds: ['story', 'feedPost'] }
      : { moment: 'catalog', assetTypeIds: ['canvasLoop', 'visualizer'] };
  }
  if (days >= 1) {
    return { moment: 'beforeRelease', assetTypeIds: ['countdown', 'story'] };
  }
  if (-days <= NEXT_UP_OUT_NOW_DAYS) {
    return { moment: 'outNow', assetTypeIds: ['story', 'visualizer'] };
  }
  return { moment: 'catalog', assetTypeIds: ['canvasLoop', 'visualizer'] };
}
